// (B) Radix Dialog(side variant) 백엔드 — antd Drawer 대체. antd Drawer v5 DOM 을 이중클래스로 미러링:
//   .om-drawer(root, 위치컨텍스트) > .om-drawer-mask(Overlay) + .om-drawer-content-wrapper(Content, placement/width)
//     > .om-drawer-content(패널, className) > [.om-drawer-header>.om-drawer-close/.om-drawer-title]/.om-drawer-body/[.om-drawer-footer]
//   각 요소에 antd 원본 .om-drawer-* 동시 부여 → 기존 소비처 SCSS(ThreadView·EmailForm·Menu) 무변경 매칭.
//   시각 base 는 styles/omDrawer.css(:where 0-특이도).
//
// ⚠ getContainer(todo tablet: 특정 컬럼 마운트) → Radix Dialog.Portal container. rootStyle position:absolute 로 루트를
//   그 컨테이너에 가둔다(mask/wrapper 는 루트 기준 absolute). ⚠ modal={false}: Dialog 와 동일(내부 antd 팝업 조작 보장),
//   Radix Overlay 는 modal={false} 에서 null 이라 마스크를 플레인 div 로 렌더(닫힘 onClick=onClose, antd maskClosable
//   기본 true), onInteractOutside preventDefault, Esc 는 Radix 기본.
import * as DialogPrimitive from '@radix-ui/react-dialog'
import type { CSSProperties } from 'react'
import type { OmwDrawerProps } from '../api/OmwDrawer'
import { cx } from './cx'
import { useFocusRestore } from './a11y/useFocusRestore'
import { useBodyScrollLock } from './a11y/bodyScrollLock'
import '../styles/omwDrawer.css'

export function PlainDrawer({
  open,
  onClose,
  title,
  width,
  placement = 'right',
  closable,
  closeIcon,
  footer,
  className,
  bodyStyle,
  getContainer,
  rootStyle,
  children,
}: OmwDrawerProps) {
  const showClose = closable !== false && closeIcon !== null
  const showHeader = title != null || showClose
  const wrapperStyle: CSSProperties | undefined = width != null ? { width } : undefined
  const container = getContainer ? getContainer() || undefined : undefined
  // 소프트 포커스 관리 + 배경 스크롤 락. 커스텀 컨테이너(특정 컬럼 마운트)로 열리면 body 락 생략(antd 파리티).
  useFocusRestore(open)
  useBodyScrollLock(open && container == null)

  return (
    <DialogPrimitive.Root
      open={open}
      modal={false}
      onOpenChange={(next) => {
        if (!next) onClose?.()
      }}
    >
      <DialogPrimitive.Portal container={container}>
        <div className={cx('om-drawer', `om-drawer-${placement}`)} style={rootStyle}>
          {/* ⚠ Radix Overlay 는 modal={false} 에서 null 을 렌더한다 → 마스크가 사라짐. 플레인 div 로 직접 렌더한다
              (닫힘은 onClick=onClose 가 담당[antd maskClosable 기본 true], data-state=open 으로 mask fade-in 유지). */}
          <div
            className="om-drawer-mask"
            data-state="open"
            aria-hidden="true"
            onClick={() => onClose?.()}
          />
          <DialogPrimitive.Content
            className="om-drawer-content-wrapper"
            data-placement={placement}
            style={wrapperStyle}
            aria-describedby={undefined}
            onInteractOutside={(e) => e.preventDefault()}
            // 닫힘 포커스 복원은 useFocusRestore 가 담당 → Radix 기본 복원과 충돌 방지.
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div className={cx('om-drawer-content', className)}>
              {showHeader && (
                <div className="om-drawer-header">
                  {showClose && (
                    <DialogPrimitive.Close className="om-drawer-close" aria-label="Close">
                      {closeIcon ?? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M3 3l10 10M13 3L3 13"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                    </DialogPrimitive.Close>
                  )}
                  {title != null && (
                    <DialogPrimitive.Title className="om-drawer-title">
                      {title}
                    </DialogPrimitive.Title>
                  )}
                </div>
              )}
              {/* Radix a11y: Title 은 항상 정확히 하나(제목 있으면 헤더 내 가시, 없으면 sr-only). */}
              {title == null && (
                <DialogPrimitive.Title className="om-drawer-sr-only"> </DialogPrimitive.Title>
              )}
              <div className="om-drawer-body" style={bodyStyle}>
                {children}
              </div>
              {footer != null && <div className="om-drawer-footer">{footer}</div>}
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
